/**
 * Reports API Client - CRUD operations for scan reports
 *
 * All calls go through the central request() function so that
 * 401 responses trigger a silent token refresh before logging out.
 */

import { request, getStoredToken } from './api';

export interface ScanScores {
  aivss?: number;
  cvss?: number;
  aars?: number;
  overall?: number;
  crs?: number;
  [key: string]: unknown;
}

export interface Report {
  scanId: string;
  userId: string;
  target: string;
  branch?: string;
  targetType: string;
  status: string;
  scores: ScanScores;
  recommendation: string;
  recommendationReason: string;
  findings: unknown[];
  findingSummary: Record<string, unknown>;
  vendorGuard?: Record<string, unknown>;
  licenseAnalysis?: Record<string, unknown>;
  aiBom?: Record<string, unknown>;
  dependencies?: Record<string, unknown>;
  metadata: Record<string, unknown>;
  scannedAt: string;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateReportData {
  target: string;
  type: string;
  scanLevel?: string;
}

export interface UpdateReportData {
  status?: string;
  scores?: ScanScores;
  findings?: unknown[];
  findingSummary?: Record<string, unknown>;
}

export const reportsApi = {
  getReports: (limit: number, offset: number, orgHeader?: Record<string, string>) =>
    request<{ reports: Report[]; total: number; limit: number; offset: number }>(
      'GET',
      `/v2/reports?limit=${limit}&offset=${offset}`,
      { token: getStoredToken(), extraHeaders: orgHeader }
    ),

  getReport: (reportId: string) =>
    request<Report>('GET', `/v2/reports/${reportId}`, { token: getStoredToken() }),

  getStats: (orgHeader?: Record<string, string>) =>
    request<{
      totalScans: number;
      completedScans: number;
      failedScans: number;
      scansThisMonth: number;
      scansRemaining: number;
      averageScanDuration: number;
      averageRiskScore: number;
      lastScanDate: string | null;
    }>('GET', '/v2/reports/stats', { token: getStoredToken(), extraHeaders: orgHeader }),

  createReport: (data: CreateReportData) =>
    request<Report>('POST', '/v2/reports', { body: data, token: getStoredToken() }),

  updateReport: (reportId: string, data: UpdateReportData) =>
    request<Report>('PUT', `/v2/reports/${reportId}`, { body: data, token: getStoredToken() }),

  deleteReport: (reportId: string) =>
    request<{ success: boolean }>('DELETE', `/v2/reports/${reportId}`, { token: getStoredToken() }),

  cleanupReports: (keepCount: number) =>
    request<{ deleted: number }>('POST', '/v2/reports/cleanup', {
      body: { keepCount },
      token: getStoredToken(),
    }),

  compareScans: (scan1: string, scan2: string) =>
    request<ComparisonResult>('POST', '/v2/scans/compare', {
      body: { scan1, scan2 },
      token: getStoredToken(),
    }),
};

export interface ComparisonScanInfo {
  scanId: string;
  target: string;
  scannedAt: string;
}

export interface ComparisonResult {
  scan1: ComparisonScanInfo | string;
  scan2: ComparisonScanInfo | string;
  comparison: {
    scoreChange?: number;
    scores?: {
      scan1: { aivss: number; cvss: number; aars: number; overall: number; crs?: number };
      scan2: { aivss: number; cvss: number; aars: number; overall: number; crs?: number };
      delta: { aivss: number; cvss: number; aars: number; overall: number; crs?: number };
    };
    findings?: {
      scan1Total: number;
      scan2Total: number;
      new: Array<{ id: string; severity: string; title: string; category: string; location?: { file?: string; line?: number } }>;
      resolved: Array<{ id: string; severity: string; title: string; category: string; location?: { file?: string; line?: number } }>;
      unchangedCount: number;
      newBySeverity: { critical: number; high: number; medium: number; low: number; info: number };
      resolvedBySeverity: { critical: number; high: number; medium: number; low: number; info: number };
    };
  };
}
